import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Button, Container } from '../components/site/ui';
import PulseSection from '../components/PulseSection';
import {
  IconArrowRight,
  IconCases,
  IconCheck,
  IconDashboard,
  IconLink,
  IconLock,
  IconMail,
  IconMerchants,
  IconSearch
} from '../components/icons';

const assurances = [
  { icon: IconLock, label: 'No integration required' },
  { icon: IconLink, label: 'Free pilot, no card needed' },
  { icon: IconMail, label: 'Signal from real customer reports' }
];

const points = [
  {
    icon: IconCases,
    title: 'Issue trends by courier',
    description:
      'See which delivery problems — damaged, delayed, not received — are showing up against your network.'
  },
  {
    icon: IconSearch,
    title: 'Postcode and hotspot signals',
    description: 'Spot the areas driving the most customer-reported issues before they become a pattern.'
  },
  {
    icon: IconMerchants,
    title: 'Merchant breakdown',
    description: 'See which retailers your customer-reported issues are concentrated around.'
  },
  {
    icon: IconDashboard,
    title: 'Trends and anomalies',
    description: 'Track how reported issue volumes move week to week, not just a single snapshot.'
  }
];

const CouriersPage = () => {
  // What the plans actually include is admin-configured, so it is read rather
  // than restated here where it could drift from the pricing page.
  const [included, setIncluded] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/couriers/pricing')
      .then(({ data }) => {
        if (cancelled) return;
        const plans = data?.courierPlanPrices || {};
        const allowance = plans.pilot?.freeCaseAllowance;
        setIncluded([
          allowance ? `Pilot: ${allowance} free cases` : 'Pilot: free cases to try it',
          ...(Array.isArray(plans.intelligence?.features) ? plans.intelligence.features : [])
        ]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
  <>
    {/* Hero */}
    <section className="bg-info-900">
      <Container width="full" className="py-12 sm:py-16">
        <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
          <div>
            <p className="text-[11px] font-700 uppercase tracking-[0.14em] text-info-300">
              For couriers
            </p>
            <h1 className="mt-3 font-display text-[34px] font-800 leading-[1.08] tracking-tight text-white sm:text-[44px]">
              See the delivery problems
              <br />
              <span className="text-info-300">customers are reporting.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/80">
              ParcelBuddy captures real customer-reported delivery issues and turns them into
              intelligence — issue trends, hotspots and patterns across your network.
            </p>

            <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
              {assurances.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2 text-sm font-medium text-white/85">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-info-500 text-white">
                    <Icon size={14} />
                  </span>
                  {label}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button as={Link} to="/couriers/start-pilot" variant="info" size="lg">
                Start free pilot
                <IconArrowRight size={18} />
              </Button>
              <Button as={Link} to="/pricing?for=couriers" variant="onDark" size="lg">
                See pricing
              </Button>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-panel">
            <p className="font-display text-[15px] font-700 text-ink-900">What you get</p>
            <p className="mt-1 text-sm text-ink-600">Start free, upgrade when you&apos;re ready.</p>
            <ul className="mt-5 space-y-2.5">
              {included.map((label) => (
                <li
                  key={label}
                  className="flex items-center gap-3 rounded-xl border border-ink-200 px-3.5 py-3"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-info-50 text-info-600">
                    <IconCheck size={16} />
                  </span>
                  <span className="text-sm font-600 text-ink-900">{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Container>
    </section>

    {/* Built from real customer reports */}
    <section className="bg-white py-12 sm:py-14">
      <Container width="full">
        <h2 className="font-display text-2xl font-800 tracking-tight text-ink-900 sm:text-[28px]">
          Built from real customer reports
        </h2>
        <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-ink-600">
          Every signal comes from customers reporting genuine delivery problems — not surveys or
          estimates.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {points.map(({ icon: Icon, title, description }) => (
            <div key={title} className="rounded-xl border border-ink-200 bg-white p-5 shadow-card">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-info-50 text-info-600 ring-1 ring-inset ring-info-100">
                <Icon size={20} />
              </span>
              <h3 className="mt-3.5 font-display text-[15px] font-700 text-ink-900">{title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{description}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>

    {/* The same Pulse panel the home page shows, with the figures that
        speak to this audience. */}
    <PulseSection
      panels={['courier']}
      title="Couriers in the Pulse"
      description="The carriers customers are reporting delivery issues against right now."
    />

    {/* Pricing CTA */}
    <section className="bg-ink-50 py-12 sm:py-14">
      <Container width="full">
        <div className="flex flex-col items-start justify-between gap-6 rounded-2xl bg-info-700 px-6 py-8 sm:px-8 lg:flex-row lg:items-center">
          <div className="max-w-xl">
            <h2 className="font-display text-2xl font-700 tracking-tight text-white">Pricing</h2>
            <p className="mt-2 text-[15px] leading-relaxed text-white/75">
              Free pilot with 7 cases, then Intelligence at a flat monthly rate. Set up by our team —
              get in touch to get started.
            </p>
          </div>
          <Button as={Link} to="/pricing?for=couriers" variant="onDark" size="lg" className="shrink-0">
            See pricing
            <IconArrowRight size={18} />
          </Button>
        </div>
      </Container>
    </section>
  </>
  );
};

export default CouriersPage;
